/**
 * @file        kybApi.js
 * @module      modules/kyb/api
 * @description Calls to the KYB backend (/api/kyb). Reuses the app's existing
 *              axios instances from src/axios/api.js, so cookies, the CSRF
 *              header and session refresh work exactly as everywhere else.
 *
 *              Every provider call happens on the backend — nothing here
 *              talks to Cashfree or Sandbox.
 *
 *              The internal simulator's knobs travel in an `x-kyb-sim` header.
 *              The backend ignores that header unless it runs in mock mode
 *              with the simulator enabled.
 */

import { httpinstance, httpinstancefile } from "../../../axios/api";
import { MSG } from "../utils/kybConstants";

let simKnobs = null;

/** Set by the simulator drawer; null in Customer builds. */
export function setSimulatorKnobs(knobs) {
  simKnobs = knobs && Object.keys(knobs).length ? knobs : null;
}

const headers = () => (simKnobs ? { "x-kyb-sim": JSON.stringify(simKnobs) } : {});

/** Normalised error: { status, code, message, field }. */
export function toKybError(err) {
  const res = err?.response;
  if (!res) return { status: 0, code: "NETWORK", message: MSG.NETWORK, field: null };
  const d = res.data || {};
  return {
    status: res.status,
    code: d.code || (res.status === 401 ? "UNAUTHORIZED" : "ERROR"),
    message: d.message || MSG.NETWORK,
    field: d.field || null,
    // API fallback: this step has failed often enough to offer documents instead.
    ...(d.fallbackAvailable === true ? { fallbackAvailable: true, fallbackReason: d.fallbackReason || null } : {}),
  };
}

const unwrap = (p) =>
  p.then(
    (r) => r.data?.data,
    (e) => {
      throw toKybError(e);
    },
  );

const base = "kyb";

export const kybApi = {
  config: () => unwrap(httpinstance.get(`${base}/config`)),
  /** body.resume = "digilocker" when coming back from DigiLocker in the same tab. */
  startOrResume: (body = {}) => unwrap(httpinstance.post(`${base}/verifications`, body, { headers: headers() })),
  restart: (body = {}) => unwrap(httpinstance.post(`${base}/verifications/restart`, body, { headers: headers() })),
  get: (id) => unwrap(httpinstance.get(`${base}/verifications/${id}`, { headers: headers() })),
  /** Business verification card on the Profile page: status, organization and person. */
  profile: () => unwrap(httpinstance.get(`${base}/profile`)),
  /** One of the Customer's own documents, as a Blob (open or save it in the browser). */
  file: (id, fileId) =>
    httpinstance.get(`${base}/verifications/${id}/files/${fileId}`, { params: { download: 1 }, responseType: "blob" }).then(
      (r) => r.data,
      (e) => {
        throw toKybError(e);
      },
    ),
  /** POST /verifications/:id/<path> */
  step: (id, path, body = {}) => unwrap(httpinstance.post(`${base}/verifications/${id}/${path}`, body, { headers: headers() })),
  upload: (id, kind, file) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/documents`, form, { headers: headers() }));
  },
  /** Stage 2 — identity document image (JPG/PNG). side: front | back */
  uploadId: (id, docType, side, file) => {
    const form = new FormData();
    form.append("docType", docType);
    form.append("side", side);
    form.append("file", file);
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/identity/alt/document`, form, { headers: headers() }));
  },
  /** Stage 2 — one captured selfie frame (Blob or File). */
  selfie: (id, file) => {
    const form = new FormData();
    form.append("file", file, file.name || "selfie.jpg");
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/identity/alt/selfie`, form, { headers: headers() }));
  },
  /** Stage 4 — resolution / authorization letter (PDF, JPG or PNG). */
  uploadAuthority: (id, kind, file) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/authority/documents`, form, { headers: headers() }));
  },
  removeAuthority: (id, fileId) => unwrap(httpinstance.delete(`${base}/verifications/${id}/authority/documents/${fileId}`, { headers: headers() })),
  /** API fallback: one document with the entered organization details. */
  uploadFallback: (id, docType, file) => {
    const form = new FormData();
    form.append("docType", docType);
    form.append("file", file);
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/fallback/documents`, form, { headers: headers() }));
  },
  removeFallback: (id, fileId) => unwrap(httpinstance.delete(`${base}/verifications/${id}/fallback/documents/${fileId}`, { headers: headers() })),
  /** Documents the KYB review team asked for (Result screen). */
  uploadRequested: (id, docType, file) => {
    const form = new FormData();
    form.append("docType", docType);
    form.append("file", file);
    return unwrap(httpinstancefile.post(`${base}/verifications/${id}/requests/documents`, form, { headers: headers() }));
  },
  removeRequested: (id, fileId) => unwrap(httpinstance.delete(`${base}/verifications/${id}/requests/documents/${fileId}`, { headers: headers() })),
  removeFile: (id, fileId) => unwrap(httpinstance.delete(`${base}/verifications/${id}/documents/${fileId}`, { headers: headers() })),
  apiLog: (id) => unwrap(httpinstance.get(`${base}/verifications/${id}/api-log`)),
  /** Development-only inspector: raw third-party requests/responses. */
  apiCalls: (id) => unwrap(httpinstance.get(`${base}/verifications/${id}/api-calls`)),
};
